import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Flatpickr from "react-flatpickr";
// El CSS de flatpickr viaja con el componente que lo usa: antes vivía en
// GestionCitas, que ya no monta ningún calendario.
import "flatpickr/dist/themes/material_blue.css";
import "flatpickr/dist/flatpickr.min.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { separarEspecialidad } from "../../utils/textoClinico";
import { LEYENDA_ESTADOS } from "../../utils/agendaCitas";
import EtiquetaResumen from "./EtiquetaResumen";
import "../../styles/CitasBarra.css";

/* ============================================================================
   BARRA DE FECHA · /gestion_citas

   Sustituye al calendario de 260px que ocupaba una columna fija de forma
   permanente para algo que se usa una vez. El calendario completo pasa a un
   popover: se abre sólo cuando hace falta.

   Anatomía literal de context/rediseño/referencia-citas-agenda.html:
   barra padding 11px 22px con gap 10; botones de 34px radio 7; flechas de
   32x34 con gap 2; selector de fecha padding 0 12 con texto 14px/500; chip de
   28px radio 14 con padding 0 6 0 10.

   QUÉ NO TRAE, Y POR QUÉ (verificado en la FASE 0, no asumido):
   · Pestañas Día / Semana / Lista -> `ListarCitaDoctoresFechas` recibe UNA
     fecha y filtra por igualdad. No hay endpoint de semana ni de lista, así
     que pintar las pestañas sería prometer algo que no existe.
   · Botón "+ Nueva cita" -> ver abajo. Sigue fuera.

   El botón "Filtros" SÍ está, desde que la FASE 3 dio color a los estados:
   filtrar por estado antes de que la agenda los mostrara no habría ayudado a
   nadie. Filtra por ATENUACIÓN, no ocultando: un cupo ocupado que se escondiera
   se vería libre, y alguien intentaría agendar encima.
   · Botón "+ Nueva cita" -> hoy una cita sólo se crea desde un cupo de la
     agenda, que aporta médico, fecha y hora. Un botón suelto abriría el modal
     sin esos datos y habría que añadirle selección de médico y hora: eso es
     cambiar el flujo de alta, que está fuera de alcance.
   ========================================================================== */

function IconoCalendario() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18" /><path d="M8 3v4" /><path d="M16 3v4" />
        </svg>
    );
}

const IconoChevron = ({ direccion = "abajo" }) => {
    const d = {
        abajo: "M6 9l6 6 6-6",
        izquierda: "M15 18l-6-6 6-6",
        derecha: "M9 18l6-6-6-6",
    }[direccion];
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={d} />
        </svg>
    );
};

function IconoFiltros() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <path d="M3 5h18" /><path d="M6 12h12" /><path d="M10 19h4" />
        </svg>
    );
}

function IconoCerrar() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
        </svg>
    );
}

/** "Domingo 6 de septiembre" — con la primera letra en mayúscula. */
export function etiquetaFecha(fecha) {
    const m = moment(fecha);
    if (!m.isValid()) return "";
    const texto = m.format("dddd D [de] MMMM");
    return texto.charAt(0).toLocaleUpperCase("es") + texto.slice(1);
}

export default function BarraFecha({
    fecha,
    onCambiarFecha,
    especialidadActiva = null,
    onQuitarEspecialidad,
    // Ids de estado visibles. `null` = todos, que es distinto de "ninguno":
    // una lista vacía significa que se han desmarcado los siete a mano.
    estadosVisibles = null,
    onCambiarEstados,
    // CITAS-RESUMEN-DIA: el resumen de TODO lo agendado ese día, ya contado
    // por el backend. Esta barra sólo lo pasa de largo.
    resumen = null,
    cuposDia = null,
    etiquetaAlcance = "",
    cargandoResumen = false,
    filtroCobertura = null,
    onFiltrarCobertura,
}) {
    const [abierto, setAbierto] = useState(false);
    const [filtrosAbierto, setFiltrosAbierto] = useState(false);
    const contenedorRef = useRef(null);
    const disparadorRef = useRef(null);
    const filtrosRef = useRef(null);
    const filtrosDisparadorRef = useRef(null);

    // Cerrar con Esc (devolviendo el foco) y con clic fuera. Mismo criterio que
    // MenuAcciones del kit: un panel que se abre se tiene que poder cerrar sin
    // ratón.
    useEffect(() => {
        if (!abierto) return undefined;

        const alPulsarTecla = (e) => {
            if (e.key === "Escape") {
                setAbierto(false);
                disparadorRef.current?.focus();
            }
        };
        const alPulsarFuera = (e) => {
            if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
                setAbierto(false);
            }
        };

        document.addEventListener("keydown", alPulsarTecla);
        document.addEventListener("mousedown", alPulsarFuera);
        return () => {
            document.removeEventListener("keydown", alPulsarTecla);
            document.removeEventListener("mousedown", alPulsarFuera);
        };
    }, [abierto]);

    useEffect(() => {
        if (!filtrosAbierto) return undefined;
        const alPulsarTecla = (e) => {
            if (e.key === "Escape") {
                setFiltrosAbierto(false);
                filtrosDisparadorRef.current?.focus();
            }
        };
        const alPulsarFuera = (e) => {
            if (filtrosRef.current && !filtrosRef.current.contains(e.target)) {
                setFiltrosAbierto(false);
            }
        };
        document.addEventListener("keydown", alPulsarTecla);
        document.addEventListener("mousedown", alPulsarFuera);
        return () => {
            document.removeEventListener("keydown", alPulsarTecla);
            document.removeEventListener("mousedown", alPulsarFuera);
        };
    }, [filtrosAbierto]);

    const activos = estadosVisibles ?? LEYENDA_ESTADOS.map((e) => e.id);
    const filtrando = estadosVisibles !== null;

    const alternarEstado = (id) => {
        const siguiente = activos.includes(id)
            ? activos.filter((x) => x !== id)
            : [...activos, id];
        // Volver a tenerlos todos marcados es lo mismo que no filtrar.
        onCambiarEstados?.(siguiente.length === LEYENDA_ESTADOS.length ? null : siguiente);
    };

    const mover = (dias) =>
        onCambiarFecha?.(moment(fecha).add(dias, "days").format("YYYY-MM-DD"));

    const esHoy = moment(fecha).isSame(moment(), "day");

    const especialidad = especialidadActiva
        ? separarEspecialidad(especialidadActiva?.especialidades?.especialidad ?? "")
        : null;

    return (
        <div className="cbarra">
            <button
                type="button"
                className="cbarra__boton"
                onClick={() => onCambiarFecha?.(moment().format("YYYY-MM-DD"))}
                disabled={esHoy}
                title={esHoy ? "Ya estás en el día de hoy" : "Ir a hoy"}
            >
                Hoy
            </button>

            <div className="cbarra__flechas">
                <button
                    type="button"
                    className="cbarra__flecha"
                    onClick={() => mover(-1)}
                    aria-label="Día anterior"
                >
                    <IconoChevron direccion="izquierda" />
                </button>
                <button
                    type="button"
                    className="cbarra__flecha"
                    onClick={() => mover(1)}
                    aria-label="Día siguiente"
                >
                    <IconoChevron direccion="derecha" />
                </button>
            </div>

            <div className="cbarra__fecha-wrap" ref={contenedorRef}>
                <button
                    type="button"
                    ref={disparadorRef}
                    className="cbarra__fecha"
                    onClick={() => setAbierto((v) => !v)}
                    aria-expanded={abierto}
                    aria-haspopup="dialog"
                >
                    <span className="cbarra__fecha-icono"><IconoCalendario /></span>
                    <span className="cbarra__fecha-texto">{etiquetaFecha(fecha)}</span>
                    <span className="cbarra__fecha-icono"><IconoChevron /></span>
                </button>

                {abierto && (
                    <div className="cbarra__popover" role="dialog" aria-label="Elegir fecha">
                        <Flatpickr
                            value={fecha}
                            onChange={(fechas) => {
                                if (!fechas?.[0]) return;
                                onCambiarFecha?.(moment(fechas[0]).format("YYYY-MM-DD"));
                                setAbierto(false);
                            }}
                            options={{
                                inline: true,
                                dateFormat: "Y-m-d",
                                disableMobile: true,
                                locale: Spanish,
                            }}
                            render={({ defaultValue, ...props }, ref) => <div ref={ref} />}
                        />
                    </div>
                )}
            </div>

            {/* CITAS-RESUMEN: pegado a la fecha a propósito -- describe el
                día que está seleccionado, y ponerlo aquí lo dice sin
                necesidad de un texto que lo explique. */}
            <EtiquetaResumen
                resumen={resumen}
                cupos={cuposDia}
                etiquetaAlcance={etiquetaAlcance}
                cargando={cargandoResumen}
                filtroCobertura={filtroCobertura}
                onFiltrarCobertura={onFiltrarCobertura}
                estadosVisibles={estadosVisibles}
                onCambiarEstados={onCambiarEstados}
            />

            <div className="cbarra__filtros-wrap" ref={filtrosRef}>
                <button
                    type="button"
                    ref={filtrosDisparadorRef}
                    className={`cbarra__boton${filtrando ? " cbarra__boton--activo" : ""}`}
                    onClick={() => setFiltrosAbierto((v) => !v)}
                    aria-expanded={filtrosAbierto}
                    aria-haspopup="dialog"
                >
                    <IconoFiltros />
                    Filtros
                    {filtrando && <span className="cbarra__contador">{activos.length}</span>}
                </button>

                {filtrosAbierto && (
                    <div className="cbarra__popover cbarra__popover--filtros" role="dialog" aria-label="Filtrar por estado">
                        <p className="cbarra__filtros-titulo">Mostrar estados</p>
                        {LEYENDA_ESTADOS.map((estado) => (
                            <label className="cbarra__filtro" key={estado.id}>
                                <input
                                    type="checkbox"
                                    checked={activos.includes(estado.id)}
                                    onChange={() => alternarEstado(estado.id)}
                                />
                                <span
                                    className={`cbarra__filtro-muestra cagenda__cita--${estado.clave}`}
                                    aria-hidden="true"
                                />
                                {estado.etiqueta}
                            </label>
                        ))}
                        {filtrando && (
                            <button
                                type="button"
                                className="cbarra__filtros-limpiar"
                                onClick={() => onCambiarEstados?.(null)}
                            >
                                Ver todos
                            </button>
                        )}
                    </div>
                )}
            </div>

            {especialidad && (
                <>
                    <span className="cbarra__separador" aria-hidden="true" />
                    <span className="cbarra__chip">
                        {especialidad.nombre}
                        {especialidad.sigla && (
                            <span className="cbarra__chip-sigla">{especialidad.sigla}</span>
                        )}
                        {onQuitarEspecialidad && (
                            <button
                                type="button"
                                className="cbarra__chip-quitar"
                                onClick={onQuitarEspecialidad}
                                aria-label={`Quitar filtro de ${especialidad.nombre}`}
                            >
                                <IconoCerrar />
                            </button>
                        )}
                    </span>
                </>
            )}
        </div>
    );
}
